import React, { forwardRef, useEffect, useRef, useState } from "react"
import { styled, Theme } from "@mui/material/styles"
import { SwipeableDrawer, SwipeableDrawerProps } from "@mui/material"
import Stack from "@mui/material/Stack"
import { BottomSheetProps } from "components/atoms/BottomSheet/bottomSheet.types"

export default function SwipeableBottomSheet(props: BottomSheetProps) {
  const {
    id,
    title,
    show,
    height,
    maxHeight,
    containerId,
    noBackdrop = false,
    drawerBleeding = 0,
    openBottomSheet,
    closeBottomSheet,
    children,
    expandedChildren
  } = props

  const [touchStartY, setTouchStartY] = useState(0)
  const [preDefinedTransformValue, setPreDefinedTransformValue] = useState<string>(`translateY(${getInitialTransformValue()}) !important`)

  const drawerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInitialTransformValueForDrawerPaper()
  }, [])

  useEffect(() => {
    setInitialTransformValueForDrawerPaper()
    setPreDefinedTransformValue("none")
  }, [drawerRef.current])

  useEffect(() => {
    if (show) return
    setInitialTransformValueForDrawerPaper()
  }, [show])

  function setInitialTransformValueForDrawerPaper() {
    if (!drawerBleeding || !height) return
    const paperEl = document.querySelectorAll(`#${containerId} .MuiDrawer-paper`)[0] as HTMLElement
    if (!paperEl) return
    const initialTransformValue = getInitialTransformValue()
    paperEl.style.transform = `translateY(${initialTransformValue})`
    paperEl.style.opacity = "1 !important"
  }

  function getInitialTransformValue() {
    if (!drawerBleeding || !height) return
    const formattedHeight = height.endsWith("px") ? parseInt(height) : Math.round(parseInt(height) * window.innerHeight * 0.01 + 15)
    const initialTransformValue = formattedHeight - drawerBleeding
    return initialTransformValue + "px"
  }

  function handleTouchStart(event: React.TouchEvent) {
    setTouchStartY(event.touches[0].clientY)
  }

  function handleTouchMove(event: React.TouchEvent) {
    const touchDistance = touchStartY - event.touches[0].clientY
    if (touchDistance < 0 && !show) {
      event.stopPropagation()
      return
    }
  }

  return (
    <StyledSwipeableDrawer
      id={id || "swipeable-drawer"}
      className="wisham-swipeable-drawer"
      ref={drawerRef}
      anchor="bottom"
      open={show}
      height={height}
      maxHeight={maxHeight}
      noBackdrop={noBackdrop ? 1 : 0}
      drawerBleeding={drawerBleeding}
      preDefinedTransformValue={preDefinedTransformValue}
      swipeAreaWidth={drawerBleeding}
      disableEnforceFocus
      allowSwipeInChildren={true}
      disableDiscovery={true}
      disableSwipeToOpen={false}
      BackdropProps={noBackdrop ? {
        invisible: true,
        style: { visibility: "hidden" },
        onClick: (e) => e.preventDefault()
      } : {}}
      container={containerId ? document.getElementById(containerId) : document.body}
      onOpen={openBottomSheet}
      onClose={closeBottomSheet}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      <StyledBox
        id="edge-wrapper"
        sx={{
          position: drawerBleeding ? "absolute" : "relative",
          top: -drawerBleeding,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          visibility: drawerBleeding ? "visible" : "unset",
          right: 0,
          left: 0
        }}
      >
        <HorizontalHandle/>
        {title && <Title>{title}</Title>}
        {children}
      </StyledBox>
      {expandedChildren && <StyledBox id="expanded-child">
        {expandedChildren}
      </StyledBox>}
    </StyledSwipeableDrawer>
  )
}

interface StyledSwipeableDrawerProps extends SwipeableDrawerProps {
  theme?: Theme;
  height?: string;
  maxHeight?: string;
  noBackdrop?: 1 | 0;
  drawerBleeding: number | undefined;
  preDefinedTransformValue: string
}

const StyledSwipeableDrawer = styled(forwardRef<HTMLDivElement, StyledSwipeableDrawerProps>(
  ({
     height,
     maxHeight,
     noBackdrop,
     drawerBleeding,
     preDefinedTransformValue,
     ...other
   }: StyledSwipeableDrawerProps, ref) => (
    <SwipeableDrawer ref={ref} {...other} />
  )
))(({ theme, height, maxHeight, noBackdrop, drawerBleeding, preDefinedTransformValue }) => ({
  maxWidth: "var(--max-container-width)",
  height: noBackdrop ? "calc(100vh - 48px)" : "100vh",
  top: "unset",
  left: "calc((100vw - var(--max-container-width)) / 2)",
  zIndex: 1300,

  "& .MuiDrawer-paper": {
    maxWidth: "var(--max-container-width)",
    height: drawerBleeding ? `calc(${height} - ${drawerBleeding}px)` : height,
    overflow: "visible !important",
    borderRadius: drawerBleeding ? "0" : "32px 32px 0 0",
    transform: preDefinedTransformValue,
    boxShadow: "none"
  },

  "& .MuiDrawer-paper div#edge-wrapper": {
    padding: "8px 12px 48px",
    borderRadius:  "32px 32px 0 0",
    height: drawerBleeding ? (height || "auto") : "100%",
    maxHeight: drawerBleeding ? (maxHeight || height || "unset") : "unset",
    overflow: "hidden",
    pointerEvents: "auto"
  },

  "@supports (height: 100dvh)": {
    height: noBackdrop ? "calc(100dvh - 48px)" : "100dvh"
  }
}))

const HorizontalHandle = styled("div")(({ theme }) => ({
  width: "55px",
  height: "6px",
  backgroundColor: theme.palette.gray["11"],
  borderRadius: "6px",
  margin: "0 auto 16px",
  flexShrink: 0
}))

const Title = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  alignItems: "center",
  fontWeight: 500,
  fontSize: "1.4rem",
  color: theme.palette.gray["16"],
  marginBottom: "12px",
  paddingBottom: "8px",
  position: "relative",

  "&::after": {
    content: "''",
    height: "1px",
    width: "100%",
    position: "absolute",
    bottom: 0,
    left: "0%",
    backgroundColor: theme?.palette.gray["5"]
  }
}))

const StyledBox = styled("div")(({ theme }) => ({
  height: "100%",
  backgroundColor: "#fff",
  zIndex: 1,
  transform: "translateY(0) !important"
}))
